import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { listWhatsappConnectionQuery } from '@/queries/whatsapp.query'
import { UpdateWhatsappDto, WhatsappService } from '@/services/whatsapp.service'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Connection } from '../schema'
import { selectSetCurrent, selectSetOpen, useConnectionStore } from '../store'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  current?: Connection
}

const formSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Nome é obrigatório.'),
})
type ConnectionsForm = z.infer<typeof formSchema>;

export function ConnectionsMutateDrawer({ open, onOpenChange, current }: Props) {
  const isUpdate = !!current;
  const form = useForm<ConnectionsForm>({
    resolver: zodResolver(formSchema),
    defaultValues: current ?? {
      name: '',
    },
  });
  const setCurrent = useConnectionStore(selectSetCurrent);
  const setOpen = useConnectionStore(selectSetOpen);
  const queryClient = useQueryClient();

  const onSubmit = form.handleSubmit(async (data: ConnectionsForm) => {
    try {
      if (isUpdate) {
        await WhatsappService.update(data as unknown as UpdateWhatsappDto);
        setOpen(null);
        toast.success("Conexão atualizada");
      } else {
        const response = await WhatsappService.create(data);
        setCurrent(response);
        setOpen('connect');
      }
    } catch (err) {
      if (err instanceof AxiosError) {
        toast.error(
          isUpdate
            ? "Ocorreu um erro ao tentar atualizar a conexão."
            : "Ocorreu um erro ao tentar criar a conexão."
        );
      }
    } finally {
      queryClient.invalidateQueries({ queryKey: listWhatsappConnectionQuery.queryKey })
      form.reset();
    }
  });


  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        form.reset();
      }}
    >
      <SheetContent className='flex flex-col'>
        <SheetHeader className='text-left'>
          <SheetTitle>{isUpdate ? 'Atualizar' : 'Criar'} Conexão</SheetTitle>
          <SheetDescription>
            {isUpdate
              ? 'atualize a conexão provendo as informações necessárias.'
              : 'adicione uma nova conexão provendo as informações necessárias. '}
            clique em adicionar quando estiver feito.
          </SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form
            id='tasks-form'
            onSubmit={onSubmit}
            className='flex-1 space-y-5'
          >
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus placeholder='digite o nome da conexão' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <SheetFooter className='gap-2'>
          <SheetClose asChild>
            <Button variant='outline'>Cancelar</Button>
          </SheetClose>
          <Button form='tasks-form' type='submit'>
            Adicionar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
